import { useCallback, useEffect, useState } from "react";

export interface Countdown {
  /** Segundos que faltan. `0` cuando no hay ninguna espera corriendo. */
  readonly remaining: number;
  /** Atajo de `remaining > 0`, que es la condición que deshabilita botones. */
  readonly isRunning: boolean;
  /** Arranca (o reinicia) la espera. */
  start: (seconds: number) => void;
}

/**
 * Una cuenta atrás en segundos, para las esperas entre acciones repetibles
 * — reenviar un correo, volver a pedir un código.
 *
 * ── Por qué guarda una FECHA LÍMITE y no un contador ────────────────
 * Lo obvio sería `setInterval(() => setRemaining(n => n - 1), 1000)`, y
 * está mal por dos razones que solo se notan en producción: `setInterval`
 * se retrasa con cada vuelta, y el navegador ESTRANGULA los temporizadores
 * de una pestaña en segundo plano —a una vez por minuto en varios—. Con
 * un contador que resta, quien deje la pestaña de lado 30 segundos vuelve
 * y todavía le faltan 28: la espera dura lo que el navegador quiso, no lo
 * que se pidió. Guardando el instante en que termina, cada latido calcula
 * lo que falta DE VERDAD contra el reloj, y volver a la pestaña muestra el
 * número correcto —o el botón ya libre— sin importar cuántos latidos se
 * perdieron.
 *
 * El latido va a 250 ms y no a 1000: solo sirve para repintar el número,
 * y a un segundo justo se ve saltar de 30 a 28 cuando el latido y el
 * segundo real caen desalineados.
 */
export function useCountdown(): Countdown {
  const [deadline, setDeadline] = useState<number | null>(null);
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    if (deadline === null) return;

    const tick = () => {
      const left = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setRemaining(left);
      // Al llegar a cero se suelta la fecha límite, y con ella el efecto
      // se vuelve a ejecutar y limpia su propio intervalo: nada sigue
      // latiendo de fondo cuando ya no hay nada que contar.
      if (left === 0) setDeadline(null);
    };

    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [deadline]);

  const start = useCallback((seconds: number) => {
    // `remaining` se pinta ya en este render, sin esperar al primer latido:
    // si no, el botón enseñaría "Espera 0 s" durante un instante justo
    // después de pulsarlo.
    setRemaining(seconds);
    setDeadline(Date.now() + seconds * 1000);
  }, []);

  return { remaining, isRunning: remaining > 0, start };
}
